import { graphql } from 'react-relay';
import React, { useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import type { FC } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import EmojiResource from '@atlaskit/emoji/emoji-resource';
import type { EmojiId } from '@atlaskit/emoji/types';
import { Reactions as AkReactions } from '@atlaskit/reactions/Reactions';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import type { Placement } from '@atlaskit/popper/main';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';

import { fg } from '@confluence/feature-gating';
import {
	ADD_REACTION_EXPERIENCE,
	DELETE_REACTION_EXPERIENCE,
	VIEW_REACTION_USER_LIST_EXPERIENCE,
	VIEW_REACTIONS_EXPERIENCE,
	ADD_QUICK_REACTION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { getLogger } from '@confluence/logger';
import { DocumentScroller } from '@confluence/scroll/entry-points/DocumentScroller.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import type { ScrollToElementFn } from '@confluence/scroll/entry-points/DocumentScroller.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { getOptimisticImageURL } from '@confluence/fabric-emoji-support';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import type {
	ReactionsContentStatusQuery as ReactionsContentStatusQuery$data,
	ReactionsContentStatusQueryVariables as ReactionsContentStatusQuery$variables,
} from './__types__/ReactionsContentStatusQuery.ts';
import type {
	ReactionsDeleteReactionMutation as ReactionsDeleteReactionMutation$data,
	ReactionsDeleteReactionMutationVariables as ReactionsDeleteReactionMutation$variables,
} from './__types__/ReactionsDeleteReactionMutation.ts';
import type {
	ReactionsConfluenceSaveReactionMutation as ReactionsConfluenceSaveReactionMutation$data,
	ReactionsConfluenceSaveReactionMutationVariables as ReactionsConfluenceSaveReactionMutation$variables,
} from './__types__/ReactionsConfluenceSaveReactionMutation.ts';
import { defaultEmojis } from './reactionConstants.ts';
import { ReactionsPlaceholder } from './ReactionsPlaceholder.tsx';
import { ReactionsSummaryViewPlaceholder } from './ReactionsSummaryViewPlaceholder.tsx';
import { ReactionsViewError } from './ReactionsViewError.tsx';
import { ReactedUsersQuery } from './graphql/ReactedUsersQuery.graphql.ts';
import { ReactionsMutationError } from './ReactionsMutationError.tsx';
import { useReactionsData } from './hooks/useReactionsData.ts';
import { useReactionsQueryParams, ReactionType } from './hooks/useReactionsQueryParams.ts';
import { getReactionsSummary, getReactionsQueryInfo } from './utils/data.ts';
import {
	getWrappingContainerByLocation,
	getContentTypeByLocation,
	getBooleanLocationTypes,
} from './utils/reactions-utils.ts';
import type {
	GQLContentType,
	GQLContainerType,
	ReactionLocation,
	ReactionContainerType,
	ReactedUsersMap,
	ReactionsData,
	ReactionsMutationResults,
	ReactionMutationUpdateFn,
	QueryData,
} from './reactionTypes.ts';
import type { ReactionsContentQuery } from './graphql/__types__/ReactionsContentQuery.ts';
import type { ReactionsCommentsQuery as ReactionsCommentsQueryType } from './graphql/__types__/ReactionsCommentsQuery.ts';
import { PAGE_REACTIONS_METRIC, FEED_REACTIONS_METRIC } from './perf.config.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type { ReactedUsersQuery as ReactedUsersQueryType } from './graphql/__types__/ReactedUsersQuery.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import type {
	ReactionsContentQuery$data,
	ReactionsContentQuery$variables,
} from './graphql/__generated__/ReactionsContentQuery.graphql.ts';
import type {
	ReactionsCommentsQuery$data,
	ReactionsCommentsQuery$variables,
} from './graphql/__generated__/ReactionsCommentsQuery.graphql.ts';

const ReactionsConfluenceSaveReactionMutation = graphql`
	mutation ReactionsConfluenceSaveReactionMutation(
		$cloudId: ID!
		$contentId: Long!
		$containerId: Long!
		$containerType: ContainerType!
		$contentType: GraphQLReactionContentType!
		$emojiId: String!
	) {
		confluence_addReaction(
			cloudId: $cloudId
			input: {
				contentId: $contentId
				containerId: $containerId
				containerType: $containerType
				contentType: $contentType
				emojiId: $emojiId
			}
		) {
			ari
			containerAri
			emojiId
		}
	}
`;
const ReactionsDeleteReactionMutation = graphql`
	mutation ReactionsDeleteReactionMutation(
		$contentId: String!
		$containerId: String!
		$containerType: ContainerType!
		$contentType: GraphQLReactionContentType!
		$emojiId: String!
	) {
		deleteReaction(
			contentId: $contentId
			containerId: $containerId
			containerType: $containerType
			contentType: $contentType
			emojiId: $emojiId
		) {
			ari
			containerAri
			emojiId
		}
	}
`;
const ReactionsContentStatusQuery = graphql`
	query ReactionsContentStatusQuery($contentId: ID) {
		content(id: $contentId, status: ["archived", "current"]) {
			nodes {
				id
				status
			}
		}
	}
`;

const getDefaultOptimisticResponse = (
	emojiId: string,
): ReactionsConfluenceSaveReactionMutation$data => ({
	__typename: 'Mutation',
	confluence_addReaction: {
		// @ts-expect-error - __typename does not exist in the generated artifacts data type but needed here.
		__typename: 'ConfluenceReactionPayload',
		ari: null,
		containerAri: null,
		emojiId,
	},
});

export type ReactionsProps = {
	contentId: string;
	containerId: string;
	containerType: ReactionContainerType;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	location: ReactionLocation;
	readOnly?: boolean;
	hidePlaceholder?: boolean;
	reactionsData?: ReactionsData;
	updateCache?: ReactionMutationUpdateFn;
	contentSubType?: string | null;
	summaryViewEnabled?: boolean;
	customAnalyticsAttributes?: Record<string, any>;
	summaryViewPlacement?: Placement;
	popularFeedTimeGranularity?: string;
	isSubtle?: boolean;
	limit?: number;
	sort?: 'asc' | 'desc';
	onlyRenderPicker?: boolean;
	reactionPickerPlacement?: Placement;
	showAddReactionText?: boolean;
	isViewOnly?: boolean;
	pickerQuickReactionEmojiIds?: EmojiId[];
	showSubtleDefaultReactions?: boolean;
	allowUserDialog?: boolean;
	noFlexWrap?: boolean;
	noRelativeContainer?: boolean;
	reactionPickerTriggerTooltipContent?: string;
	reactionPickerTriggerIcon?: React.ReactNode;
	allowSelectFromSummaryView?: boolean;
	summaryViewThreshold?: number;
	useDefaultStyledContainer?: boolean;
	useButtonAlignmentStyling?: boolean;
	reactionPickerTriggerText?: string;
	hoverableSummaryView?: boolean;
	isListItem?: boolean;
	summaryButtonIconAfter?: React.ReactNode;
	optimisticallyLoadSummaryEmojis?: boolean;
	getOptimisticResponse?: {
		(params: {
			ari: string;
			containerAri: string;
			emojiId: string;
			action: 'add';
		}): ReactionsConfluenceSaveReactionMutation$data | undefined;
		(params: {
			ari: string;
			containerAri: string;
			emojiId: string;
			action: 'delete';
		}): ReactionsDeleteReactionMutation$data | undefined;
	};
	particleEffectByEmoji?: Record<string, boolean>;
	onParticleEffectChange?: (emojiId: string, show: boolean) => void;
	renderParticleEffectOnSummaryView?: boolean;
};

const logger = getLogger('reactions');
const noop = () => {};

const reactionsContainerStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtle',
});

type ReactionSummary = {
	count: number;
	emojiId: string;
	id: string;
	reacted: boolean;
};
export const Reactions: FC<ReactionsProps> = ({
	contentId,
	containerId,
	containerType,
	location,
	readOnly,
	hidePlaceholder,
	reactionsData,
	updateCache,
	contentSubType,
	summaryViewEnabled = false,
	customAnalyticsAttributes = {},
	summaryViewPlacement = 'top-end',
	popularFeedTimeGranularity,
	isSubtle = false,
	limit,
	sort,
	onlyRenderPicker = false,
	showAddReactionText = true,
	isViewOnly = false,
	pickerQuickReactionEmojiIds,
	showSubtleDefaultReactions,
	allowUserDialog = true,
	noFlexWrap = false,
	noRelativeContainer = false,
	reactionPickerTriggerIcon,
	reactionPickerTriggerTooltipContent,
	reactionPickerTriggerText,
	allowSelectFromSummaryView,
	summaryViewThreshold,
	useDefaultStyledContainer,
	useButtonAlignmentStyling,
	reactionPickerPlacement,
	hoverableSummaryView = false,
	isListItem = false,
	summaryButtonIconAfter,
	optimisticallyLoadSummaryEmojis = false,
	getOptimisticResponse,
	particleEffectByEmoji: externalParticleEffectByEmoji,
	onParticleEffectChange,
	renderParticleEffectOnSummaryView = false,
}) => {
	const scrollFunctionRef = useRef<ScrollToElementFn>(() => void 0);
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const firstRenderRef = useRef(false);
	const activeReactionsRef = useRef<string[]>([]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, cloudId, activationId } = useSessionData();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const [{ searchSessionId, additionalAnalytics: searchSessionIdAnalytics }] = useSearchSessionId();
	const [reactedUsersMap, setReactedUsersMap] = useState<ReactedUsersMap>({});
	const [internalParticleEffectByEmoji, setInternalParticleEffectByEmoji] = useState<
		Record<string, boolean>
	>({});
	const [addReactionErrorState, setAddReactionErrorState] = useState<Error>();
	const [deleteReactionErrorState, setDeleteReactionErrorState] = useState<Error>();
	const timeoutHandle = useRef<ReturnType<typeof setTimeout>>();

	const {
		focusedPageReactionsId,
		focusedCommentReactionsId,
		focusedCommentQuickReactionsId,
		focusedPageQuickReactionsId,
		quickReactionsEmojiId,
		removeReactionsQueryParams,
	} = useReactionsQueryParams();

	const [state] = useContentState();

	const contentType = getContentTypeByLocation(location);
	const { isComment, isPage, isFeed } = getBooleanLocationTypes(location);
	const [addReaction] = useMutationInterop<
		ReactionsConfluenceSaveReactionMutation$data,
		ReactionsConfluenceSaveReactionMutation$variables
	>(ReactionsConfluenceSaveReactionMutation);
	const [deleteReaction] = useMutationInterop<
		ReactionsDeleteReactionMutation$data,
		ReactionsDeleteReactionMutation$variables
	>(ReactionsDeleteReactionMutation);

	const useNetworkOnly =
		(focusedCommentReactionsId === contentId || focusedCommentQuickReactionsId === contentId) &&
		!firstRenderRef.current;

	const {
		data: { reactions, totalCount, ari, containerAri },
		loading,
		error,
	} = useReactionsData({
		location,
		contentId,
		containerId,
		inheritedReactionsData: reactionsData,
		reactedUsersMap,
		useNetworkOnly,
		limit,
		sort,
	});

	// Container for content types are adding extra margin which is affecting the new reaction styles (sidebar entrypoint/byline) so want to return default for this location
	const StyledContainer = getWrappingContainerByLocation(
		useDefaultStyledContainer ? undefined : location,
	);

	const perfSegment = isPage ? PAGE_REACTIONS_METRIC : isFeed ? FEED_REACTIONS_METRIC : null;

	useEffect(() => {
		firstRenderRef.current = true;

		if (perfSegment) {
			perfSegment.start();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const emojiProvider = useMemo(
		() =>
			getEmojiProvider(EmojiResource, {
				cloudId,
				userId,
				// disabled b/c uploading is not playing nicely with reactions
				disableUpload: true,
			}),
		[cloudId, userId],
	);

	// For focused reactions, open the reaction picker menu
	// For quick reactions, add the reaction to the focused comment from the query params
	// state.contentLoading checks if the slowest content is loaded as to not displace reactions
	// do not use useLayoutEffect => delays render that messes up the document scroller
	useEffect(() => {
		(async () => {
			if (Object.entries(state.contentLoading).length === 0 && !loading) {
				// 1. Try overlay/hover picker first
				if (onlyRenderPicker && isComment && focusedCommentReactionsId === contentId) {
					const overlayContainerId = `#reactions-container-${location}-${contentId}-onlyRenderPicker`;
					const overlayContainer = document.querySelector(overlayContainerId) as HTMLElement;

					const reactionsButton = overlayContainer.querySelector(
						"span[aria-label='Add reaction']",
					) as HTMLElement;

					reactionsButton?.click();
					return;
				}

				// 2. Fallback to main bar logic
				if (isPage && focusedPageReactionsId === contentId) {
					const containerId = `#reactions-container-${location}-${contentId}`;
					const reactionsContentContainer = document.querySelector(containerId) as HTMLElement;

					if (reactionsContentContainer) {
						// We only need to scroll when the page reaction query parameter is set otherwise
						// we leverage the already existing scroll logic for page/inline comments
						if (scrollFunctionRef.current && isPage) {
							scrollFunctionRef.current(reactionsContentContainer);
						}

						const reactionsButton = reactionsContentContainer.querySelector(
							"span[aria-label='Add reaction']",
						) as HTMLElement;

						// Open the reactions container
						reactionsButton?.click();
						return;
					}
				} else if (
					!readOnly &&
					quickReactionsEmojiId &&
					((isComment && focusedCommentQuickReactionsId === contentId) ||
						(isPage && focusedPageQuickReactionsId === contentId))
				) {
					const emojiResource = await Promise.resolve(emojiProvider);
					const foundEmoji = await emojiResource.findById(quickReactionsEmojiId);

					if (isPage) {
						const reactionsContentContainer = document.querySelector(
							`#reactions-container-${location}-${contentId}`,
						) as HTMLElement;

						if (reactionsContentContainer) {
							if (scrollFunctionRef.current) {
								scrollFunctionRef.current(reactionsContentContainer);
							}
						}
					}

					if (!foundEmoji) {
						logger.log`Invalid query parameters provided for the 'addReaction' action`;
						return;
					}

					onReactionsSelection(quickReactionsEmojiId, true);
					removeReactionsQueryParams();
				}
			}
		})().catch((error) => {
			logger.log`An error occurred while attempting to perform the reaction action from query parameters: ${error}`;
		});
		// Omitting scrollFunctionRef.current from the dependency list b/c it causes extra renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contentId,
		location,
		focusedPageReactionsId,
		focusedCommentReactionsId,
		focusedCommentQuickReactionsId,
		focusedPageQuickReactionsId,
		quickReactionsEmojiId,
		loading,
		isPage,
		isComment,
		state.contentLoading,
		readOnly,
		onlyRenderPicker,
	]);

	/**
	 * Separating the content status query from the reactions query b/c we need the
	 * content status for all display types (except feed), regardless of having reactionsData.
	 * Archiving/unarchiving a page only updates the page content status, so we
	 * always need to use the page ID when querying
	 */
	const { data: contentStatusData, error: contentStatusError } = useQueryInterop<
		ReactionsContentStatusQuery$data,
		ReactionsContentStatusQuery$variables
	>(ReactionsContentStatusQuery, {
		variables: {
			contentId: isComment ? containerId : contentId,
		},
		skip: isFeed,
	});

	const getUpdatedReactionsResponse = useCallback(
		(data: QueryData | null, emojiId: string, actionType: 'add' | 'delete') => {
			const summary = getReactionsSummary(data);

			if (!summary) {
				return;
			}

			let updatedCount = summary.reactionsCount ?? 0;
			const updatedReactions = [...(summary.reactionsSummaryForEmoji ?? [])] as ReactionSummary[];

			const reactionObject = updatedReactions.find((item) => item?.emojiId === emojiId);

			if (actionType === 'add') {
				// Check if the reaction already exists
				if (reactionObject && !reactionObject.reacted) {
					reactionObject.count++;
					reactionObject.reacted = true;
				} else {
					// If this type of reaction doesn't exist, create a new one
					const newReaction = {
						emojiId,
						count: 1,
						reacted: true,
						id: `${summary.containerAri}|${summary.ari}|${emojiId}`,
						__typename: 'ReactionsSummaryForEmoji',
					};

					updatedReactions.push(newReaction);
				}

				// Increment the total count of reactions
				updatedCount++;
			}

			if (actionType === 'delete') {
				// Remove the logged in users' reaction
				if (reactionObject && reactionObject.reacted && reactionObject.count > 1) {
					reactionObject.count--;
					reactionObject.reacted = false;
				} else {
					// If this is the last reaction of this type, remove it completely
					const removeIdx = updatedReactions.map((item) => item?.emojiId).indexOf(emojiId);

					updatedReactions.splice(removeIdx, 1);
				}

				// Decrement the total count of reactions
				updatedCount--;
			}

			const getContentResponseData = () => {
				// Use type guards to ensure we have the correct data structure
				if (data && 'content' in data && data.content?.nodes?.[0]) {
					const contentData = data as ReactionsContentQuery;
					if (contentData.content?.nodes) {
						return {
							...contentData,
							content: {
								...contentData.content,
								nodes: [
									{
										...contentData.content.nodes[0],
										contentReactionsSummary: {
											...summary,
											reactionsSummaryForEmoji: updatedReactions,
											reactionsCount: updatedCount,
										},
									},
								],
							},
						} as QueryData;
					}
				}
				return data;
			};

			// Create the new updated response for the type of reaction and return it
			if (isComment && data && 'comments' in data && data.comments?.nodes?.[0]) {
				const commentsData = data as ReactionsCommentsQueryType;
				if (commentsData.comments?.nodes) {
					return {
						...commentsData,
						comments: {
							...commentsData.comments,
							nodes: [
								{
									...commentsData.comments.nodes[0],
									reactionsSummary: {
										...summary,
										reactionsSummaryForEmoji: updatedReactions,
										reactionsCount: updatedCount,
									},
								},
							],
						},
					} as QueryData;
				}
			}

			return getContentResponseData();
		},
		[isComment],
	);

	const updateApolloCache = useCallback(
		(emojiId: string, actionType: 'add' | 'delete', onCacheUpdateError: (Error) => void) =>
			(_cache, result: ReactionsMutationResults) => {
				// If the mutation fails, don't do anything - shouldn't happen but satisfies TypeScript
				if (!result) {
					return;
				}

				// Skip this step if a parent component is already handling this
				if (Boolean(reactionsData) && updateCache) {
					updateCache(emojiId, actionType, contentId, containerId, result, onCacheUpdateError);
					return;
				}

				const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, containerId);

				try {
					const response = readQueryInterop<
						ReactionsContentQuery$data | ReactionsCommentsQuery$data,
						ReactionsContentQuery$variables | ReactionsCommentsQuery$variables
					>({
						query,
						variables: queryVariables,
					});

					const updatedResponse = getUpdatedReactionsResponse(response, emojiId, actionType);

					if (!updatedResponse) {
						return;
					}

					writeQueryInterop<
						ReactionsContentQuery$data | ReactionsCommentsQuery$data,
						ReactionsContentQuery$variables | ReactionsCommentsQuery$variables
					>({
						query,
						variables: queryVariables,
						data: updatedResponse as ReactionsContentQuery$data | ReactionsCommentsQuery$data,
					});
				} catch (err) {
					logger.error`An Error occurred when updating cache for ${location} reaction - ${err}`;
					if (err instanceof Error) {
						onCacheUpdateError(err);
					}
				}
			},
		[
			contentId,
			containerId,
			isComment,
			location,
			getUpdatedReactionsResponse,
			updateCache,
			reactionsData,
		],
	);

	const handleRetryMutation = () => {
		if (addReactionErrorState) {
			// Reset the mutation error state
			setAddReactionErrorState(undefined);
		}

		if (deleteReactionErrorState) {
			// Reset the mutation error state
			setDeleteReactionErrorState(undefined);
		}

		/**
		 * In the future we can retry the mutation,
		 * however the memoized function for normalizing reaction data doesn't
		 * react to changes in the apollo client cache soon enough. More investigation needed.
		 */
	};

	// Use external state if provided, otherwise use internal state
	const particleEffectByEmoji = externalParticleEffectByEmoji || internalParticleEffectByEmoji;

	const updateParticleEffect = useCallback(
		(emojiId: string, show: boolean) => {
			if (onParticleEffectChange) {
				onParticleEffectChange(emojiId, show);
			} else {
				setInternalParticleEffectByEmoji((prev) => ({
					...prev,
					[emojiId]: show,
				}));
			}
		},
		[onParticleEffectChange],
	);

	const handleAddReactionMutationError = useCallback(
		(addMutationError: Error, emojiIdx: number, quickReaction?: boolean) => {
			setAddReactionErrorState(addMutationError);

			experienceTracker.stopOnError({
				name: ADD_REACTION_EXPERIENCE,
				error: addMutationError,
			});

			// Only manipulate activeReactionsRef when getOptimisticResponse is not provided
			if (!getOptimisticResponse && emojiIdx !== -1) {
				activeReactionsRef.current.splice(emojiIdx, 1);
			}

			if (quickReaction) {
				experienceTracker.stopOnError({
					name: ADD_QUICK_REACTION_EXPERIENCE,
					error: addMutationError,
				});
			}
		},
		[experienceTracker, setAddReactionErrorState, getOptimisticResponse],
	);

	const handleAddReactionCacheError = useCallback(
		(emojiIdx: number, quickReaction?: boolean) => (addMutationCacheError: Error) => {
			handleAddReactionMutationError(addMutationCacheError, emojiIdx, quickReaction);
		},
		[handleAddReactionMutationError],
	);

	const onReactionsSelection = useCallback(
		(emojiId: string, quickReaction?: boolean) => {
			if (focusedCommentReactionsId === contentId) {
				removeReactionsQueryParams({ reactionType: ReactionType.COMMENT });
			}
			if (addReactionErrorState || deleteReactionErrorState) {
				setAddReactionErrorState(undefined);
				setDeleteReactionErrorState(undefined);
			}

			// Skip activeReactionsRef checks when getOptimisticResponse is provided
			if (!getOptimisticResponse && activeReactionsRef.current.includes(emojiId)) {
				return;
			}

			const existingReaction = reactions.find((r) => r.emojiId === emojiId);
			if (existingReaction && existingReaction.reacted) {
				return;
			}

			// Only manage activeReactionsRef when getOptimisticResponse is not provided
			const emojiIdx = getOptimisticResponse ? -1 : activeReactionsRef.current.length;
			if (!getOptimisticResponse) {
				activeReactionsRef.current.push(emojiId);
			}

			// Only add particle effects if not in summary view
			if (!summaryViewEnabled) {
				updateParticleEffect(emojiId, true);
			}

			if (quickReaction) {
				experienceTracker.start({
					name: ADD_QUICK_REACTION_EXPERIENCE,
				});
			}

			experienceTracker.start({
				name: ADD_REACTION_EXPERIENCE,
				attributes: {
					type: location,
					placement: customAnalyticsAttributes.reactionsPlacement,
				},
			});

			void addReaction({
				variables: {
					cloudId,
					contentId: Number(contentId),
					contentType: contentType?.toUpperCase() as GQLContentType,
					containerId: Number(containerId),
					containerType: containerType?.toUpperCase() as GQLContainerType,
					emojiId,
				},
				update: updateApolloCache(
					emojiId,
					'add',
					handleAddReactionCacheError(emojiIdx, quickReaction),
				),
				optimisticResponse: Boolean(updateCache)
					? getOptimisticResponse?.({ ari, containerAri, emojiId, action: 'add' })
					: getDefaultOptimisticResponse(emojiId),
			})
				.then(() => {
					experienceTracker.succeed({
						name: ADD_REACTION_EXPERIENCE,
					});

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'created',
							actionSubject: 'reaction',
							source: contentType,
							attributes: {
								objectId: contentId,
								objectType: contentType,
								containerId,
								containerType,
								emojiId,
								activationId,
								accountId: userId,
								displayType: location,
								isLivePage: contentSubType === 'live',
								reactionsStyle: isSubtle ? 'subtle' : 'classic',
								searchSessionId,
								...searchSessionIdAnalytics,
								...customAnalyticsAttributes,
								popularFeedTimeGranularity,
							},
						},
					}).fire();

					// Only manipulate activeReactionsRef when getOptimisticResponse is not provided
					if (!getOptimisticResponse && emojiIdx !== -1) {
						activeReactionsRef.current.splice(emojiIdx, 1);
					}

					if (quickReaction) {
						experienceTracker.succeed({
							name: ADD_QUICK_REACTION_EXPERIENCE,
						});

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'created',
								actionSubject: 'quickReaction',
								source: contentType,
								attributes: {
									objectId: contentId,
									objectType: contentType,
									containerId,
									containerType,
									emojiId,
									activationId,
									accountId: userId,
									displayType: location,
									isLivePage: contentSubType === 'live',
									reactionsStyle: isSubtle ? 'subtle' : 'classic',
									...customAnalyticsAttributes,
								},
							},
						}).fire();
					}
				})
				.catch((error) => {
					handleAddReactionMutationError(error, emojiIdx, quickReaction);
				});
		},
		[
			focusedCommentReactionsId,
			contentId,
			addReactionErrorState,
			deleteReactionErrorState,
			reactions,
			summaryViewEnabled,
			experienceTracker,
			location,
			customAnalyticsAttributes,
			addReaction,
			cloudId,
			contentType,
			containerId,
			containerType,
			updateApolloCache,
			handleAddReactionCacheError,
			updateCache,
			getOptimisticResponse,
			ari,
			containerAri,
			removeReactionsQueryParams,
			updateParticleEffect,
			createAnalyticsEvent,
			activationId,
			userId,
			contentSubType,
			isSubtle,
			searchSessionId,
			searchSessionIdAnalytics,
			popularFeedTimeGranularity,
			handleAddReactionMutationError,
		],
	);

	const handleDeleteReactionMutationError = useCallback(
		(deleteMutationError: Error, emojiIdx: number) => {
			setDeleteReactionErrorState(deleteMutationError);

			experienceTracker.stopOnError({
				name: DELETE_REACTION_EXPERIENCE,
				error: deleteMutationError,
			});

			// Only manipulate activeReactionsRef when getOptimisticResponse is not provided
			if (!getOptimisticResponse && emojiIdx !== -1) {
				activeReactionsRef.current.splice(emojiIdx, 1);
			}
		},
		[experienceTracker, setDeleteReactionErrorState, getOptimisticResponse],
	);

	const handleDeleteReactionCacheError = useCallback(
		(emojiIdx: number) => (deleteMutationCacheError: Error) => {
			handleDeleteReactionMutationError(deleteMutationCacheError, emojiIdx);
		},
		[handleDeleteReactionMutationError],
	);

	/* onReactionClick - A callback to handle when the user clicks on a reaction that has already been made
	 *
	 * NOTE: If the user has reacted already we will call the delete mutation, otherwise
	 *       we call the function that handles the add mutation logic
	 */

	const onReactionClick = useCallback(
		(emojiId: string) => {
			// Skip activeReactionsRef checks when getOptimisticResponse is provided
			if (!getOptimisticResponse && activeReactionsRef.current.includes(emojiId)) {
				return;
			}

			if (addReactionErrorState || deleteReactionErrorState) {
				// the user might retry deleting a reaction without using the provided error link
				setDeleteReactionErrorState(undefined);
				setAddReactionErrorState(undefined);
			}

			const existingReaction = reactions.find((r) => r.emojiId === emojiId);

			// If the current user has NOT reacted with this emoji, call the add function
			if (!existingReaction?.reacted) {
				onReactionsSelection(emojiId);
				return;
			}

			// Only manage activeReactionsRef when getOptimisticResponse is not provided
			const emojiIdx = getOptimisticResponse ? -1 : activeReactionsRef.current.length;
			if (!getOptimisticResponse) {
				activeReactionsRef.current.push(emojiId);
			}

			// Otherwise, we need to delete their reaction
			experienceTracker.start({
				name: DELETE_REACTION_EXPERIENCE,
				attributes: {
					type: location,
					placement: customAnalyticsAttributes.reactionsPlacement,
				},
			});

			updateParticleEffect(emojiId, false);

			void deleteReaction({
				variables: {
					contentId,
					contentType: contentType?.toUpperCase() as GQLContentType,
					containerId,
					containerType: containerType?.toUpperCase() as GQLContainerType,
					emojiId,
				},
				update: updateApolloCache(emojiId, 'delete', handleDeleteReactionCacheError(emojiIdx)),
				optimisticResponse: getOptimisticResponse?.({
					ari,
					containerAri,
					emojiId,
					action: 'delete',
				}),
			})
				.then(() => {
					experienceTracker.succeed({
						name: DELETE_REACTION_EXPERIENCE,
					});

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'deleted',
							actionSubject: 'reaction',
							source: contentType,
							attributes: {
								objectId: contentId,
								objectType: contentType,
								containerId,
								containerType,
								emojiId,
								activationId,
								accountId: userId,
								displayType: location,
								isLivePage: contentSubType === 'live',
								reactionsStyle: isSubtle ? 'subtle' : 'classic',
								...customAnalyticsAttributes,
							},
						},
					}).fire();

					// Only manipulate activeReactionsRef when getOptimisticResponse is not provided
					if (!getOptimisticResponse && emojiIdx !== -1) {
						activeReactionsRef.current.splice(emojiIdx, 1);
					}
				})
				.catch((error) => {
					handleDeleteReactionMutationError(error, emojiIdx);
				});
		},
		[
			addReactionErrorState,
			deleteReactionErrorState,
			reactions,
			experienceTracker,
			location,
			customAnalyticsAttributes,
			updateParticleEffect,
			deleteReaction,
			contentId,
			contentType,
			containerId,
			containerType,
			updateApolloCache,
			handleDeleteReactionCacheError,
			getOptimisticResponse,
			ari,
			containerAri,
			onReactionsSelection,
			createAnalyticsEvent,
			activationId,
			userId,
			contentSubType,
			isSubtle,
			handleDeleteReactionMutationError,
		],
	);

	const onReactionHover = useCallback(
		(emojiId: string) => {
			// Skip activeReactionsRef checks when getOptimisticResponse is provided
			if (
				(!getOptimisticResponse && activeReactionsRef.current.includes(emojiId)) ||
				reactions.length === 0
			) {
				return;
			}

			if (timeoutHandle.current) {
				clearTimeout(timeoutHandle.current);
			}

			timeoutHandle.current = setTimeout(() => {
				experienceTracker.start({
					name: VIEW_REACTION_USER_LIST_EXPERIENCE,
					attributes: {
						contentId,
						type: location,
						emojiId,
						placement: customAnalyticsAttributes.reactionsPlacement,
					},
				});

				void queryInterop<ReactedUsersQueryType>({
					query: ReactedUsersQuery,
					variables: {
						cloudId,
						contentId,
						contentType: contentType?.toUpperCase() as GQLContentType,
						containerId,
						containerType: containerType?.toUpperCase() as GQLContainerType,
						emojiId,
					},
					fetchPolicy: 'network-only', // We need to fetch the latest list every hover so we don't miss new reactions
				})
					.then((res: { data: ReactedUsersQueryType }) => {
						if (!res.data.reactedUsers) {
							return;
						}
						const { emojiId: reactedEmojiId, confluencePerson } = res.data.reactedUsers;

						// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
						const users = confluencePerson.filter(Boolean).map((user) => ({
							id: user?.accountId ?? '',
							displayName: user?.displayName ?? '',
						}));

						const updatedReactedUsersMap = { ...reactedUsersMap };
						updatedReactedUsersMap[reactedEmojiId] = users;

						experienceTracker.succeed({
							name: VIEW_REACTION_USER_LIST_EXPERIENCE,
							attributes: {
								numReactions: users.length,
							},
						});

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'hovered',
								actionSubject: 'existingReaction',
								source: contentType,
								attributes: {
									objectId: contentId,
									containerId,
									displayType: location,
									emojiId,
									numReactions: users.length,
									isLivePage: contentSubType === 'live',
									reactionsStyle: isSubtle ? 'subtle' : 'classic',
									...customAnalyticsAttributes,
								},
							},
						}).fire();

						setReactedUsersMap(updatedReactedUsersMap);
					})
					.catch((error) => {
						logger.error`An error occured when user list for reaction - ${error}`;
						experienceTracker.stopOnError({
							name: VIEW_REACTION_USER_LIST_EXPERIENCE,
							error,
						});
					});
			}, 500);
		},
		[
			getOptimisticResponse,
			reactions.length,
			experienceTracker,
			contentId,
			location,
			customAnalyticsAttributes,
			cloudId,
			contentType,
			containerId,
			containerType,
			reactedUsersMap,
			createAnalyticsEvent,
			contentSubType,
			isSubtle,
		],
	);

	const updateReactionEvents = (event: UIAnalyticsEvent) => {
		const newAttributes = {
			objectId: contentId,
			containerId,
			displayType: location,
			...customAnalyticsAttributes,
		};

		const updatedPayload = {
			...event.payload,
			attributes: { ...event.payload.attributes, ...newAttributes },
		};

		event.update(updatedPayload);
	};

	// WS-3494 - We interpret reactionsData being null as an error from the parent query
	const viewError =
		reactionsData === null ? new Error('Reactions failed to load from parent query') : error;

	const isArchivedPage = contentStatusData?.content?.nodes?.[0]?.status === 'archived';

	// Disable add and delete reactions for archived pages and anonymous users
	const isDisabled = !isReactionsEnabled || isArchivedPage || !userId || false;

	// Delay reactions if slowest content is still loading and query params are present
	const delayForQueryParams =
		Object.entries(state.contentLoading).length > 0 && focusedPageReactionsId === contentId;

	const quickReactionEmojis = {
		ari,
		containerAri,
		emojiIds: defaultEmojis,
	};

	// Analytic event for opening Reactions Dialog
	const onDialogOpenCallback = useCallback(
		(emojiId: string, source?: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'reactionsDialog',
					source: source || contentType,
					attributes: {
						objectId: contentId,
						objectType: contentType,
						containerId,
						containerType,
						displayType: location,
						isLivePage: contentSubType === 'live',
						reactionsStyle: isSubtle ? 'subtle' : 'classic',
						emojiId,
						...customAnalyticsAttributes,
					},
				},
			}).fire();
		},
		[
			containerId,
			containerType,
			contentId,
			contentType,
			contentSubType,
			isSubtle,
			createAnalyticsEvent,
			location,
			customAnalyticsAttributes,
		],
	);

	// Analytic event for closing Reactions Dialog
	const onDialogCloseCallback = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'reactionsDialogCloseButton',
				source: contentType,
				attributes: {
					objectId: contentId,
					objectType: contentType,
					containerId,
					containerType,
					displayType: location,
					isLivePage: contentSubType === 'live',
					reactionsStyle: isSubtle ? 'subtle' : 'classic',
					...customAnalyticsAttributes,
				},
			},
		}).fire();
	}, [
		containerId,
		containerType,
		contentId,
		contentType,
		contentSubType,
		isSubtle,
		createAnalyticsEvent,
		location,
		customAnalyticsAttributes,
	]);

	// Analytic event for selecting an emoji from the Reactions Dialog emoji list
	const onDialogSelectReactionCallback = useCallback(
		(emojiId: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'selected',
					actionSubject: 'reactionsDialogEmojiList',
					source: contentType,
					attributes: {
						objectId: contentId,
						objectType: contentType,
						containerId,
						containerType,
						displayType: location,
						isLivePage: contentSubType === 'live',
						reactionsStyle: isSubtle ? 'subtle' : 'classic',
						emojiId,
						...customAnalyticsAttributes,
					},
				},
			}).fire();
		},
		[
			containerId,
			containerType,
			contentId,
			contentType,
			contentSubType,
			isSubtle,
			createAnalyticsEvent,
			location,
			customAnalyticsAttributes,
		],
	);

	// Analytic event when changing pages inside the dialog
	const onDialogPageChangeCallback = useCallback(
		(emojiId: string, currentPage: number, maxPages: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'reactionsDialogPageNavigation',
					source: contentType,
					attributes: {
						objectId: contentId,
						objectType: contentType,
						containerId,
						containerType,
						displayType: location,
						isLivePage: contentSubType === 'live',
						reactionsStyle: isSubtle ? 'subtle' : 'classic',
						emojiId,
						currentPage,
						maxPages,
						...customAnalyticsAttributes,
					},
				},
			}).fire();
		},
		[
			containerId,
			containerType,
			contentId,
			contentType,
			contentSubType,
			isSubtle,
			createAnalyticsEvent,
			location,
			customAnalyticsAttributes,
		],
	);

	const showPlaceholderComponent = (loading || delayForQueryParams) && !hidePlaceholder;
	const showViewErrorComponent = !showPlaceholderComponent && viewError;
	const showReactionsComponent = !showPlaceholderComponent && !showViewErrorComponent;

	const reactionsSummaryGetOptimisticImageURL = useCallback(
		(emojiId: string) => {
			return getOptimisticImageURL(cloudId, emojiId, 'MDPI');
		},
		[cloudId],
	);
	const renderReactionsPlaceholder = () => {
		if (summaryViewEnabled) {
			return <ReactionsSummaryViewPlaceholder />;
		}
		return (
			<ReactionsPlaceholder
				useAccuratePills={isPage}
				onlyRenderPicker={onlyRenderPicker}
				customAnalyticsAttributes={customAnalyticsAttributes}
			/>
		);
	};

	return (
		<TransparentErrorBoundary attribution={Attribution.COLLABORATION}>
			<ExperienceStart
				key={String(contentId) + String(location)}
				name={VIEW_REACTIONS_EXPERIENCE}
				attributes={{
					type: location,
					placement: customAnalyticsAttributes.reactionsPlacement,
				}}
			/>
			{contentStatusError && <ErrorDisplay error={contentStatusError} />}
			{showPlaceholderComponent && renderReactionsPlaceholder()}
			{showViewErrorComponent && <ReactionsViewError location={location} error={viewError} />}
			{showReactionsComponent && (
				<>
					<StyledContainer
						id={`reactions-container-${location}-${contentId}${onlyRenderPicker ? '-onlyRenderPicker' : ''}`}
						data-testid={
							isDisabled
								? 'disabled-reactions-container'
								: readOnly
									? 'read-only-reactions-container'
									: 'reactions-container'
						}
						data-vc="reactions-container"
						isDisabled={isDisabled}
						readOnly={readOnly}
						hasReactions={totalCount > 0}
					>
						<Flex alignItems="center" xcss={[reactionsContainerStyles]}>
							<AnalyticsListener channel="fabric-elements" onEvent={updateReactionEvents}>
								<AkReactions
									// AI emoji generation requires a page content ID. Comment reaction IDs must not be used.
									contentId={isPage ? contentId : undefined}
									reactions={reactions}
									status={(isDisabled || readOnly ? 'LOADING' : 'READY') as any} // TODO use ReactionStatus
									loadReaction={noop}
									getReactionDetails={readOnly ? noop : onReactionHover}
									onSelection={isDisabled || readOnly ? noop : onReactionsSelection}
									onReactionClick={isDisabled || readOnly ? noop : onReactionClick}
									emojiProvider={emojiProvider}
									allowAllEmojis
									quickReactionEmojis={location === 'page' ? quickReactionEmojis : undefined}
									onDialogOpenCallback={onDialogOpenCallback}
									onDialogCloseCallback={onDialogCloseCallback}
									onDialogSelectReactionCallback={onDialogSelectReactionCallback}
									onDialogPageChangeCallback={onDialogPageChangeCallback}
									allowUserDialog={allowUserDialog}
									particleEffectByEmoji={particleEffectByEmoji}
									summaryViewEnabled={summaryViewEnabled}
									summaryViewPlacement={summaryViewPlacement}
									// Summary view is a floating button, so we want buttons w/ opaque backgrounds
									showOpaqueBackground={summaryViewEnabled}
									subtleReactionsSummaryAndPicker={isSubtle}
									showAddReactionText={showAddReactionText && isSubtle && reactions.length === 0}
									hideDefaultReactions={isSubtle}
									ProfileCardWrapper={ProfileCardWrapper}
									reactionPickerPlacement={reactionPickerPlacement}
									isViewOnly={isViewOnly || isArchivedPage}
									onlyRenderPicker={onlyRenderPicker}
									reactionPickerTriggerTooltipContent={reactionPickerTriggerTooltipContent}
									reactionPickerTriggerIcon={reactionPickerTriggerIcon}
									// If the summary view threshold is not passed in, we will use the platform summary view threshold default of 3
									reactionPickerTriggerText={
										!onlyRenderPicker &&
										summaryViewEnabled &&
										reactions.length >= (summaryViewThreshold ?? 3)
											? reactionPickerTriggerText
											: undefined
									}
									pickerQuickReactionEmojiIds={
										hoverableSummaryView && reactions.length > 0 ? [] : pickerQuickReactionEmojiIds
									}
									showSubtleDefaultReactions={showSubtleDefaultReactions}
									allowSelectFromSummaryView={allowSelectFromSummaryView}
									hoverableSummaryView={isReactionsEnabled && hoverableSummaryView}
									summaryViewThreshold={summaryViewThreshold}
									noRelativeContainer={noRelativeContainer}
									noWrap={noFlexWrap}
									useButtonAlignmentStyling={useButtonAlignmentStyling}
									isListItem={isListItem}
									summaryGetOptimisticImageURL={
										optimisticallyLoadSummaryEmojis
											? reactionsSummaryGetOptimisticImageURL
											: undefined
									}
									getOptimisticImageURL={
										fg('platform_reactions_optimistic_url')
											? reactionsSummaryGetOptimisticImageURL
											: undefined
									}
									summaryButtonIconAfter={summaryButtonIconAfter}
									renderParticleEffectOnSummaryView={renderParticleEffectOnSummaryView}
								/>
							</AnalyticsListener>
						</Flex>
						<ExperienceSuccess name={VIEW_REACTIONS_EXPERIENCE} />
						{perfSegment && !loading && <PageSegmentLoadEnd metric={perfSegment} key={contentId} />}
						<DocumentScroller>
							{({ scrollToElement }) => {
								scrollFunctionRef.current = scrollToElement;
								return null;
							}}
						</DocumentScroller>
						<ReactionsMutationError
							isPage={isPage}
							resetErrorStates={handleRetryMutation}
							addMutationError={addReactionErrorState}
							deleteMutationError={deleteReactionErrorState}
						/>
					</StyledContainer>
				</>
			)}
		</TransparentErrorBoundary>
	);
};
